import { ContextualMenuItemType, type IContextualMenuItem } from '@fluentui/react';
import { Button } from '@h2oai/ui-kit';
import React from 'react';

import { ListRegisteredModelsSupportedFilterOrderKeys } from '../../../mlops/mlops.storage.functions';
import { NONE_OPTION_KEY, noneOption } from '../../../utils/render';

export type ModelsOrderByKeys = Extract<ListRegisteredModelsSupportedFilterOrderKeys, 'created_time' | 'name'>;

const orderKeyToText: Record<ModelsOrderByKeys, string> = {
  created_time: `Created Time`,
  name: `Display Name`,
};

const sortByMenuItems: IContextualMenuItem[] = Object.entries(orderKeyToText).map(([key, text]) => ({ key, text }));

const orderMenuItems: IContextualMenuItem[] = [
  {
    key: `ascending`,
    text: `Ascending`,
  },
  {
    key: `descending`,
    text: `Descending`,
  },
];

export type ModelsOrderByProps = {
  className?: string;
  descending: boolean;
  onChange: (key: ModelsOrderByKeys | undefined, descending: boolean) => void;
  orderKey: ModelsOrderByKeys | undefined;
};

export const ModelsOrderBy = (props: ModelsOrderByProps) => {
  const { descending, onChange, orderKey } = props;
  const onMenuItemClick = (_ev: any, menuItems: IContextualMenuItem | undefined) => {
    if (!menuItems) return;
    const key = menuItems.key;
    if (key === NONE_OPTION_KEY) {
      onChange(undefined, false);
      return;
    }
    if (key === 'ascending' || key === 'descending') {
      onChange(orderKey, key === 'descending');
      return;
    }
    if (orderKeyToText[key]) {
      onChange(key as ModelsOrderByKeys, descending);
      return;
    }
  };
  const menuItems = React.useMemo<IContextualMenuItem[]>(() => {
    const sortByMenuItemsWithActive = sortByMenuItems.map((item) => {
      const isActive = item.key === orderKey;
      return {
        ...item,
        onClick: onMenuItemClick,
        iconProps: isActive ? { iconName: 'CheckMark' } : undefined,
      };
    });
    const orderMenuItemsWithActive = orderMenuItems.map((item) => {
      const isActive = item.key === (descending ? 'descending' : 'ascending');
      return {
        ...item,
        disabled: !orderKey,
        onClick: onMenuItemClick,
        iconProps: isActive ? { iconName: 'CheckMark' } : undefined,
      };
    });

    return [
      {
        key: 'sortBy',
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          topDivider: true,
          bottomDivider: true,
          title: 'Sort By',
          items: [
            {
              ...noneOption,
              onClick: onMenuItemClick,
            },
            ...sortByMenuItemsWithActive,
          ],
        },
      },
      {
        key: `order`,
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          title: `Order`,
          items: orderMenuItemsWithActive,
        },
      },
    ];
  }, [descending, orderKey]);

  return (
    <Button
      className={props.className}
      menuIconProps={{ iconName: !orderKey ? `Sort` : descending ? `Descending` : `Ascending` }}
      menuItems={menuItems}
      text={(orderKey && orderKeyToText[orderKey]) || `Sort By`}
    />
  );
};
